import { removeDupes } from '../utils/helpers';

const getAssociationFromPayload = payload => payload.chat_id || payload.message_id || payload.file_id || payload.task_id || payload.entity_key;
const getMessageKey = message => (message.chat || message.thread || message.file || message.task || message.doc) + message.type + message.author + message.text;

const sort = messages => messages.sort((m1, m2) => new Date(m2.created_at) - new Date(m1.created_at));

const messages = (state = {
    items: [],
    fetchInfo: {
        association: {}
    }
}, action) => {
    if (action.error) {
        return state;
    }

    switch (action.type) {
        case 'TEAM_CHANGE':
        case 'RESET_STORE':
        case 'MARK_ALL_FOR_REFETCH':
            return {
                items: [],
                fetchInfo: {
                    association: {}
                }
            };

        case 'MESSAGES_FETCH_REQUESTED':
            if (action.payload.silent) {
                return state;
            }

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    association: {
                        ...state.fetchInfo.association,
                        [getAssociationFromPayload(action.payload)]: {
                            ...(state.fetchInfo.association[getAssociationFromPayload(action.payload)] || {}),
                            loading: true
                        }
                    }
                }
            });

        case 'MESSAGES_FETCH_SUCCEEDED': {
            if (!action.payload.messages) {
                return state;
            }

            const noNewMessages = action.payload.messages.every(message => state.items.some(m => m.id === message.id));
            if (noNewMessages && action.payload.silent) {
                return state;
            }

            const messages = noNewMessages ? state.items : sort(removeDupes([
                ...action.payload.messages,
                ...state.items
            ]));

            return Object.assign({}, state, {
                items: messages,
                fetchInfo: {
                    ...state.fetchInfo,
                    association: {
                        ...state.fetchInfo.association,
                        [getAssociationFromPayload(action.payload)]: {
                            ...state.fetchInfo.association[getAssociationFromPayload(action.payload)],
                            [action.payload.after_id || action.payload.after_date ? 'hasAfter' : 'hasBefore']: action.payload.has_more,
                            loading: false,
                            fetched: true
                        }
                    }
                }
            });
        }

        case 'MESSAGES_FETCH_FAILED':
            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    association: {
                        ...state.fetchInfo.association,
                        [action.payload.chat_id]: {
                            ...state.fetchInfo.association[getAssociationFromPayload(action.payload)],
                            loading: false,
                            error: true
                        }
                    }
                }
            });

        case 'MESSAGE_FETCH_REQUESTED':
            if (state.items.some(message => message.id === action.payload.messageId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items, {
                    id: action.payload.messageId,
                    loading: true
                }]
            });

        case 'MESSAGE_FETCH_SUCCEEDED':
            return Object.assign({}, state, {
                items: [...state.items].map(message => {
                    if (message.id === action.payload.message.id) {
                        return action.payload.message;
                    }

                    return message;
                })
            });

        case 'MESSAGE_SEND_REQUESTED':
        case 'MESSAGE_ADD_SOCKET': {
            // For messages received from sockets
            if ((action.payload.message.files && action.payload.message.files.length) || (action.payload.message.id && state.items.some(message => message.id === action.payload.message.id))) {
                return state;
            }

            const messageKey = getMessageKey(action.payload.message);
            let items = removeDupes([action.payload.message, ...state.items.filter(message => !(message.error && messageKey === getMessageKey(message)))]);

            if (action.payload.message.thread) {
                items = items.map(message => {
                    if (action.payload.message.thread === message.id) {
                        return {
                            ...message,
                            replies: (message.replies || 0) + 1,
                            repliers: [...(new Set([...(message.repliers || []), action.payload.message.author]))]
                        }
                    }

                    return message;
                });
            }

            return Object.assign({}, state, { items });
        }

        case 'MESSAGE_SEND_FAILED':
            return Object.assign({}, state, {
                items: [...state.items]
                    .map(message => {
                        if (message === action.payload.fragment) {
                            return Object.assign({}, message, {
                                error: true,
                                fragment: action.payload.fragment
                            });
                        }

                        return message;
                    })
            });

        case 'MESSAGE_REJECT':
            return Object.assign({}, state, {
                items: state.items.filter(message => message.id !== action.payload.id)
            });

        case 'MESSAGE_SEND_SUCCEEDED':
            if (!action.payload.message) {
                return state;
            }

            return Object.assign({}, state, {
                items: state.items
                    .filter(message => message.id !== action.payload.message.id)
                    .map(message => {
                        if (message === action.payload.fragment) {
                            return Object.assign({}, message, {
                                ...action.payload.message,
                                id: action.payload.message.id
                            });
                        }

                        return message;
                    })
            });

        case 'MESSAGE_UPDATE_REQUESTED':
        case 'MESSAGE_UPDATE_SOCKET': {
            const messageProps = action.payload.messageProps || action.payload.message;

            return Object.assign({}, state, {
                items: [...state.items]
                    .map(message => {
                        if (message.id === action.payload.messageId) {
                            return {
                                ...message,
                                ...messageProps
                            };
                        }

                        return message;
                    })
            });
        }

        case 'MESSAGE_REMOVE_REQUESTED':
        case 'MESSAGE_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items]
                    .map(message => {
                        if (message.id === action.payload.messageId) {
                            return {
                                ...message,
                                is_deleted: true
                            };
                        }

                        return message;
                    })
            });

        case 'MESSAGE_SEE_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items]
                    .map(message => {
                        if (message.id === action.payload.messageId) {
                            return {
                                ...message,
                                seen_by: [...new Set([...(message.seen_by || []), action.payload.userId])]
                            };
                        }

                        return message;
                    })
            });

        case 'MESSAGES_SEE_SOCKET': {
            if (!action.payload.messageIds.length) {
                return state;
            }

            const lastMessageId = action.payload.messageIds[action.payload.messageIds.length - 1];
            const lastMessage = state.items.find(message => message.id === lastMessageId);

            return Object.assign({}, state, {
                items: [...state.items]
                    .map(message => {
                        if (
                            action.payload.messageIds.includes(message.id) ||
                            (
                                lastMessage &&
                                (!message.seen_by || !message.seen_by.includes(action.payload.userId)) &&
                                message.chat === lastMessage.chat &&
                                message.thread === lastMessage.thread &&
                                message.file === lastMessage.file &&
                                message.task === lastMessage.task &&
                                message.doc === lastMessage.doc &&
                                message.author !== action.payload.userId &&
                                new Date(message.created_at) < new Date(lastMessage.created_at)
                            )
                        ) {
                            return {
                                ...message,
                                seen_by: [...new Set([...(message.seen_by || []), action.payload.userId])]
                            };
                        }

                        return message;
                    })
            });
        }

        case 'MESSAGE_SET_REACTION_REQUESTED':
        case 'MESSAGE_SET_REACTION_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items]
                    .map(message => {
                        if (message.id === action.payload.messageId) {
                            if (action.payload.reaction) {
                                return {
                                    ...message,
                                    reactions: [...(message.reactions || []).filter(({ member_id }) => member_id !== action.payload.userId), {
                                        member_id: action.payload.userId,
                                        value: action.payload.reaction
                                    }]
                                }
                            }

                            return {
                                ...message,
                                reactions: (message.reactions || []).filter(({ member_id }) => member_id !== action.payload.userId)
                            }
                        }

                        return message;
                    })
            });

        case 'MESSAGE_SEE_SUCCEEDED':
            return Object.assign({}, state, {
                items: [...state.items]
                    .map(message => {
                        if (action.payload.messageIds.includes(message.id)) {
                            return {
                                ...message,
                                seen_by: [...new Set([...(message.seen_by || []), action.payload.userId])]
                            };
                        }

                        return message;
                    })
            });

        default:
            return state;
    }
}

export default messages;
